import { useAuth0 } from '@auth0/auth0-react';
import useSWRMutation from 'swr/mutation';
import { AgentStreamEvent } from '../types';
import { assessmentsApiUrl } from '@/AppSettings';
import ResourceNotFoundError from '@/Utils/HTTP/Errors/ResourceNotFoundError';
import http from '@/Utils/HTTP/NewHttpUtils';

// ── Session management ────────────────────────────────────────────────────────

export interface AgentChatSessionResponse {
    sessionId: string;
    status: string;
    createdAt: string;
}

export const useGetSession = (recruitmentId: string) => {
    const { getAccessTokenSilently } = useAuth0();
    return useSWRMutation(
        `recruitments/${recruitmentId}/agent-chat/sessions`,
        async (path: string): Promise<AgentChatSessionResponse | null> => {
            try {
                return await http.get<AgentChatSessionResponse>({
                    path,
                    accessToken: await getAccessTokenSilently(),
                });
            } catch (error) {
                if (error instanceof ResourceNotFoundError) {
                    return null;
                }
                throw error;
            }
        },
    );
};

export const useCreateSession = (recruitmentId: string) => {
    const { getAccessTokenSilently } = useAuth0();
    return useSWRMutation(
        `recruitments/${recruitmentId}/agent-chat/sessions`,
        async (path: string): Promise<AgentChatSessionResponse> =>
            await http.post<AgentChatSessionResponse>({
                path,
                accessToken: await getAccessTokenSilently(),
            }),
    );
};

export const useStartSearch = (recruitmentId: string) => {
    const { getAccessTokenSilently } = useAuth0();
    return useSWRMutation(
        `recruitments/${recruitmentId}/agent-chat/sessions/start-search`,
        async (_key: string, { arg: sessionId }: { arg: string }) => {
            await http.post({
                path: `agent-chat/sessions/${sessionId}/start-search`,
                accessToken: await getAccessTokenSilently(),
            });
        },
    );
};

// ── Sending user events ───────────────────────────────────────────────────────

export type SendUserEventRequest = { type: 'user.message'; text: string } | { type: 'user.interrupt' };

export const useSendUserEvent = (recruitmentId: string, sessionId: string | null) => {
    const { getAccessTokenSilently } = useAuth0();
    return useSWRMutation(
        sessionId ? `agent-chat/sessions/${sessionId}/events` : null,
        async (path: string, { arg }: { arg: SendUserEventRequest }) => {
            await http.post({
                path,
                accessToken: await getAccessTokenSilently(),
                body: arg,
            });
        },
    );
};

// ── Session events history ────────────────────────────────────────────────────

export const useGetSessionEvents = (recruitmentId: string) => {
    const { getAccessTokenSilently } = useAuth0();
    return useSWRMutation(
        `recruitments/${recruitmentId}/agent-chat/sessions/session-events`,
        async (_key: string, { arg: sessionId }: { arg: string }): Promise<AgentStreamEvent[]> => {
            const response = await http.get<AgentStreamEvent[]>({
                path: `agent-chat/sessions/${sessionId}/events`,
                accessToken: await getAccessTokenSilently(),
            });

            return response || [];
        },
    );
};

// ── SSE stream ────────────────────────────────────────────────────────────────
// Each SSE line is: data: <json> — the backend serializes BetaManagedAgentsStreamSessionEvents directly.

export async function* streamEvents(
    sessionId: string,
    accessToken: string,
    signal: AbortSignal,
): AsyncGenerator<AgentStreamEvent> {
    const response = await fetch(`${assessmentsApiUrl}agent-chat/sessions/${sessionId}/stream`, {
        method: 'GET',
        headers: {
            Authorization: `bearer ${accessToken}`,
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache',
        },
        signal,
    });

    if (!response.ok || !response.body) {
        throw new Error(`Stream failed: ${response.statusText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            for (const line of lines) {
                const trimmed = line.trim();
                if (!trimmed.startsWith('data:')) continue;
                const data = trimmed.slice(5).trim();
                if (!data || data === '[DONE]') continue;
                try {
                    yield JSON.parse(data) as AgentStreamEvent;
                } catch {
                    // malformed SSE event — skip
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}
